import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageTransition } from '@/components/PageTransition';

const VerifyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  usePageTitle('Verify Email');
  const [verificationStatus, setVerificationStatus] = useState('pending'); // pending, success, error
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    // Check if there's a token in the URL (user clicked email link)
    const token = searchParams.get('token');
    const type = searchParams.get('type');
    const tokenHash = searchParams.get('token_hash');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    console.log('VerifyPage params:', {
      token,
      type,
      tokenHash,
      error,
      errorDescription,
      fullURL: window.location.href
    });

    // Handle Supabase auth errors
    if (error) {
      setVerificationStatus('error');
      setMessage(errorDescription || 'Verification failed. Please try again.');
      return;
    }

    // Handle token verification
    if (token && type === 'signup') {
      console.log('Attempting to verify with token and type=signup');
      handleEmailVerification(token);
    } else if (tokenHash && type) {
      // Handle token_hash format (older Supabase format)
      console.log('Attempting to verify with token_hash');
      handleEmailVerificationWithHash(tokenHash, type);
    } else {
      console.log('No valid token parameters found, showing default check email screen');
    }
  }, [searchParams]);

  const handleEmailVerification = async (token) => {
    try {
      setVerificationStatus('pending');
      setMessage('Verifying your email...');

      // Use the token directly with verifyOtp
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'email'
      });

      if (error) {
        console.error('Verification error:', error);
        setVerificationStatus('error');
        setMessage(error.message || 'Invalid or expired verification link.');
      } else {
        setVerificationStatus('success');
        setMessage('Email verified successfully! Redirecting to login...');

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('Verification exception:', err);
      setVerificationStatus('error');
      setMessage('An error occurred during verification.');
    }
  };

  const handleEmailVerificationWithHash = async (tokenHash, type) => {
    try {
      setVerificationStatus('pending');
      setMessage('Verifying your email...');

      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type
      });

      if (error) {
        console.error('Verification error:', error);
        setVerificationStatus('error');
        setMessage(error.message || 'Invalid or expired verification link.');
      } else {
        setVerificationStatus('success');
        setMessage('Email verified successfully! Redirecting to login...');

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('Verification exception:', err);
      setVerificationStatus('error');
      setMessage('An error occurred during verification.');
    }
  };

  const handleResendEmail = async () => {
    setIsResending(true);

    // Get email from localStorage (stored during signup)
    const signupEmail = localStorage.getItem('signupEmail');

    if (!signupEmail) {
      setMessage('Please sign up again to receive a verification email.');
      setIsResending(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: signupEmail
      });

      if (error) {
        setMessage('Failed to resend email. Please try again.');
      } else {
        setMessage('Verification email sent! Please check your inbox.');
      }
    } catch (err) {
      setMessage('An error occurred. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-8 pt-8">
              <div className="mb-6">
                {verificationStatus === 'pending' && (
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                    <Mail className="h-10 w-10 text-blue-600" />
                  </div>
                )}
                {verificationStatus === 'success' && (
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                )}
                {verificationStatus === 'error' && (
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                    <XCircle className="h-10 w-10 text-red-600" />
                  </div>
                )}
              </div>

              <CardTitle className="text-2xl font-bold text-slate-900">
                {verificationStatus === 'pending' && 'Check your email'}
                {verificationStatus === 'success' && 'Email Verified!'}
                {verificationStatus === 'error' && 'Verification Failed'}
              </CardTitle>

              <CardDescription className="text-slate-600 mt-3 text-base">
                {verificationStatus === 'pending' && (
                  <>
                    We've sent a confirmation email to your inbox.
                    <br />
                    Please click the link in the email to verify your account.
                  </>
                )}
                {verificationStatus === 'success' && message}
                {verificationStatus === 'error' && message}
              </CardDescription>
            </CardHeader>

            <CardContent className="pb-8">
              {verificationStatus === 'pending' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Tip:</strong> Check your spam folder if you don't see the email in your inbox.
                    </p>
                  </div>

                  <div className="pt-4 space-y-3">
                    <Button
                      onClick={handleResendEmail}
                      disabled={isResending}
                      variant="outline"
                      className="w-full"
                    >
                      {isResending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Resending...
                        </>
                      ) : (
                        'Resend verification email'
                      )}
                    </Button>

                    <Button
                      onClick={() => navigate('/login')}
                      variant="ghost"
                      className="w-full"
                    >
                      Back to login
                    </Button>
                  </div>

                  {message && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm mt-4">
                      {message}
                    </div>
                  )}
                </div>
              )}

              {verificationStatus === 'success' && (
                <div className="text-center">
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full"
                  >
                    Continue to Login
                  </Button>
                </div>
              )}

              {verificationStatus === 'error' && (
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/signup')}
                    className="w-full"
                  >
                    Sign up again
                  </Button>

                  <Button
                    onClick={() => navigate('/login')}
                    variant="ghost"
                    className="w-full"
                  >
                    Back to login
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-8">
            Need help? <a href="mailto:support@aitrainingmatrix.com" className="text-blue-600 hover:text-blue-500">Contact support</a>
          </p>
        </div>
      </div>
    </PageTransition>
  );
};

export default VerifyPage;